import { Search } from 'lucide-react'
import { parseAsInteger, parseAsString, useQueryState } from 'nuqs'
import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { ExpandableCell } from '@/components/backoffice/expandable-cell'
import { ExpandableHead } from '@/components/backoffice/expandable-head'
import * as Input from '@/components/input'
import { getVisiblePages } from '@/components/pagination/get-visible-pages'
import {
  Pagination,
  PaginationButton,
  PaginationContent,
  PaginationCurrentPage,
  PaginationItemsPerPage,
  PaginationNext,
  PaginationPrevious,
} from '@/components/pagination/pagination'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/select'
import { Skeleton } from '@/components/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/table'
import { formatNumberToCurrency } from '@/helper/currency'
import { useDebouncedQueryState } from '@/hooks/use-debounce-queryState'
import { useExpandableNames } from '@/hooks/use-expandable-name'
import { GetAgentIndicationDataByIndicationIdResponse } from '@/models/agent'
import {
  ExpandedLinkedCommissionsNames,
  LinkedCommissionsResponse,
} from '@/models/backoffice/indications'
import { SponsorshipStatusResponse } from '@/models/sponsorship'
import { Indication } from '@/models/user'
import { SponsorshipStatusBadge } from '@/pages/backoffice/agent/indications/components/sponsorship-status-badge'
import { IndicationDetails } from '@/pages/dashboard/agent/components/indication-details'
import api from '@/services/api'
import { getAgentIndicationDataByIndicationId } from '@/services/get-agent indication-data-by-indicationId'
import { FormatLocaleDate } from '@/utils/format-locale-date'

import { LoaderCircle } from '../loader-circle'

const theadArray = [
  'Patrocinador',
  'Início do Contrato',
  'Fim do Contrato',
  'Status Patrocínio',
  'Comissão (%)',
  'Valor Calculado (R$)',
  'Parcelas',
]

export interface DialogIndication extends Indication {
  typeIndication: string
}

export default function CommissionsList() {
  const { indicationId } = useParams<{ indicationId: string }>()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [sponsorshipStatus, setSponsorshipStatus] = useState<
    SponsorshipStatusResponse[] | null
  >(null)
  const [sponsorships, setSponsorships] =
    useState<LinkedCommissionsResponse | null>(null)

  const [agentIndicationData, setAgentIndicationData] =
    useState<GetAgentIndicationDataByIndicationIdResponse | null>(null)

  const { expanded, getMaxLength, toggleExpanded } = useExpandableNames<
    keyof ExpandedLinkedCommissionsNames
  >({ nome_patrocinador: 20, nome_propriedade: 20 })

  const listSponsorshipsNames =
    sponsorships?.info_patrocinio.map(
      (sponsorship) => sponsorship.nome_patrocinador,
    ) ?? []

  const listPropertiesNames =
    sponsorships?.info_patrocinio.map((sponsorship) => {
      return sponsorship.nome_propriedade
    }) ?? []

  const [currentPage, setCurrentPage] = useQueryState(
    'page',
    parseAsInteger.withDefault(1),
  )
  const [itemsPerPage, setItemsPerPage] = useQueryState(
    'itemsPerPage',
    parseAsString.withDefault('10'),
  )

  const [search, setSearch] = useDebouncedQueryState(
    'search',
    parseAsString.withDefault(''),
    500,
  )

  const getSponsorshipStatus = useCallback(async () => {
    await api
      .get<SponsorshipStatusResponse[]>('/sponsorship/statuses')
      .then(({ data }) => setSponsorshipStatus(data))
      .catch((error) => toast.error(error.response.data.message))
  }, [])

  const fetchAgentIndicationDataByIndicationId = useCallback(async () => {
    if (!indicationId) return

    const { data, error, message } = await getAgentIndicationDataByIndicationId(
      { indicationId, includeReason: true },
    )

    if (error) {
      return toast.error(message)
    }

    setAgentIndicationData(data)
  }, [indicationId])

  const getLinkedSponsorshipsByIdentificationId = useCallback(
    async (sponsorshipStatusId?: string) => {
      setIsLoading(true)
      let path = `/agent/referral/sponsorships/${indicationId}/${itemsPerPage}?page=${currentPage}`
      const hasSponsorshipStatusId = sponsorshipStatusId !== undefined

      if (hasSponsorshipStatusId) {
        path = path.concat(`&sponsorshipStatus=${sponsorshipStatusId}`)
      }

      if (search) {
        path = path.concat(`&filter=${search}`)
      }

      await api
        .get<LinkedCommissionsResponse>(path)
        .then(({ data }) => setSponsorships(data))
        .catch((error) => toast.error(error.response.data.message))
        .finally(() => setIsLoading(false))
    },
    [indicationId, itemsPerPage, currentPage, search],
  )

  const numberOfPages = Math.ceil(
    (sponsorships?.total_comissoes || 0) / Number(itemsPerPage),
  )

  function handleNextButton() {
    setCurrentPage((state) => (state < numberOfPages ? state + 1 : state))
  }

  function handlePreviousButton() {
    setCurrentPage((state) => (state > 1 ? state - 1 : state))
  }

  useEffect(() => {
    if (indicationId) {
      Promise.all([
        getLinkedSponsorshipsByIdentificationId(),
        fetchAgentIndicationDataByIndicationId(),
      ])
    }
  }, [
    indicationId,
    getLinkedSponsorshipsByIdentificationId,
    fetchAgentIndicationDataByIndicationId,
  ])

  useEffect(() => {
    if (sponsorships && sponsorships.info_patrocinio.length > 0) {
      getSponsorshipStatus()
    }
  }, [getSponsorshipStatus, sponsorships])

  return (
    <div className="bg-monochromatic-200 flex grow flex-col">
      <div className="layout-padding mx-auto w-full space-y-8 p-10">
        {!agentIndicationData ? (
          <Skeleton className="h-16 w-full" />
        ) : (
          <IndicationDetails agentIndicationData={agentIndicationData} />
        )}

        <div className="space-y-4 rounded-lg bg-white px-6 py-8">
          <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
            <div className="flex items-center gap-2">
              <h3 className="text-xl sm:text-2xl">Comissões</h3>
              {isLoading && <LoaderCircle />}
            </div>
            <div className="flex flex-1 flex-col items-center gap-4 md:flex-row md:justify-end">
              <Input.Root className="md:basis-2/5">
                <Input.Prefix>
                  <Search className="text-monochromatic-300" />
                </Input.Prefix>
                <Input.Control
                  placeholder="Digite para filtrar a tabela"
                  defaultValue={search}
                  onChange={(e) => {
                    setCurrentPage(1)
                    setSearch(e.target.value)
                  }}
                  data-testid="backoffice-sponsors-filter-table-input"
                />
              </Input.Root>

              <Select
                onValueChange={(value) => {
                  const selectedValue = value === 'none' ? '' : value
                  setCurrentPage(1)
                  getLinkedSponsorshipsByIdentificationId(selectedValue)
                }}
                defaultValue="none"
              >
                <SelectTrigger
                  className="border-gray-300 sm:w-48"
                  disabled={!sponsorshipStatus || isLoading}
                  data-testid="agent-commissions-list-status-filter-select"
                >
                  <SelectValue placeholder="Filtrar por status" />
                </SelectTrigger>
                <SelectContent className="z-50">
                  <SelectItem
                    value="none"
                    data-testid="agent-commissions-list-status-filter-select-item-all"
                  >
                    Todos os status
                  </SelectItem>
                  {sponsorshipStatus?.map(({ descricao, id_status: id }) => {
                    return (
                      <SelectItem
                        key={id}
                        value={id}
                        data-testid={`agent-commissions-list-status-filter-select-item-${id}`}
                      >
                        {descricao}
                      </SelectItem>
                    )
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>

          {sponsorships?.info_patrocinio.length === 0 ? (
            <p
              className="text-center"
              data-testid="agent-commissions-list-empty-state"
            >
              Nenhuma comissão encontrada.
            </p>
          ) : (
            <Table isNotBackoffice className="table-auto">
              <TableHeader>
                <TableRow>
                  <TableHead style={{ width: 24 }} />
                  <TableHead data-testid="agent-commissions-list-table-header-patrocinador">
                    <div className="flex items-center gap-1">
                      Patrocinador
                      <ExpandableHead
                        isExpanded={expanded.nome_patrocinador}
                        minorIcon
                        onClick={(e) => {
                          e.stopPropagation()
                          toggleExpanded(
                            listSponsorshipsNames,
                            'nome_patrocinador',
                          )
                        }}
                        data-testid="agent-commissions-list-table-header-patrocinador-expandable-head"
                      />
                    </div>
                  </TableHead>
                  <TableHead>
                    <div className="flex items-center gap-1">
                      Propriedade
                      <ExpandableHead
                        isExpanded={expanded.nome_propriedade}
                        minorIcon
                        onClick={(e) => {
                          e.stopPropagation()
                          toggleExpanded(
                            listPropertiesNames,
                            'nome_propriedade',
                          )
                        }}
                        data-testid="agent-commissions-list-table-header-property-expandable-head"
                      />
                    </div>
                  </TableHead>
                  {theadArray.slice(1).map((th) => (
                    <TableHead
                      key={th}
                      data-testid={`agent-commissions-list-table-header-${th.toLowerCase().replace(/\s+/g, '-').replace(/[()%]/g, '')}`}
                    >
                      {th}
                    </TableHead>
                  ))}
                  <TableHead style={{ width: 24 }} />
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={9}>
                      <Skeleton
                        className="h-12 w-full rounded-xs"
                        data-testid="agent-commissions-list-table-skeleton"
                      />
                    </TableCell>
                  </TableRow>
                ) : (
                  sponsorships?.info_patrocinio.map((sponsorship) => (
                    <TableRow
                      key={sponsorship.id_contrato}
                      data-testid={`agent-commissions-list-table-row-${sponsorship.id_contrato}`}
                    >
                      <TableCell />
                      <TableCell
                        data-testid={`agent-commissions-list-table-cell-patrocinador-${sponsorship.id_contrato}`}
                      >
                        <ExpandableCell
                          isExpanded={expanded.nome_patrocinador}
                          name={sponsorship.nome_patrocinador}
                          maxLength={getMaxLength('nome_patrocinador')}
                        />
                      </TableCell>
                      <TableCell
                        data-testid={`agent-commissions-list-table-cell-properties-${sponsorship.id_contrato}`}
                      >
                        <ExpandableCell
                          isExpanded={expanded.nome_propriedade}
                          name={sponsorship.nome_propriedade}
                          maxLength={getMaxLength('nome_propriedade')}
                        />
                      </TableCell>
                      <TableCell
                        data-testid={`agent-commissions-list-table-cell-inicio-contrato-${sponsorship.id_contrato}`}
                      >
                        {FormatLocaleDate(sponsorship.data_inicial ?? '') ||
                          '-'}
                      </TableCell>
                      <TableCell
                        data-testid={`agent-commissions-list-table-cell-fim-contrato-${sponsorship.id_contrato}`}
                      >
                        {FormatLocaleDate(sponsorship.data_fim ?? '') || '-'}
                      </TableCell>
                      <TableCell
                        data-testid={`agent-commissions-list-table-cell-status-patrocinio-${sponsorship.id_contrato}`}
                      >
                        <SponsorshipStatusBadge
                          variant={sponsorship.status_patrocinio.id_status}
                          description={sponsorship.status_patrocinio.descricao}
                        />
                      </TableCell>

                      <TableCell
                        data-testid={`agent-commissions-list-table-cell-comissao-${sponsorship.id_contrato}`}
                      >
                        {sponsorship.porcentagem_comissao}%
                      </TableCell>
                      <TableCell
                        data-testid={`agent-commissions-list-table-cell-valor-calculado-${sponsorship.id_contrato}`}
                      >
                        {formatNumberToCurrency(sponsorship.valor_comissao)}
                      </TableCell>
                      <TableCell
                        data-testid={`agent-commissions-list-table-cell-parcelas-${sponsorship.id_contrato}`}
                      >
                        <Link
                          to={`/agent/commissions/${indicationId}/installments/${sponsorship.id_comissao}`}
                          className="text-bios-primary underline"
                          data-testid={`agent-commissions-list-table-cell-parcelas-link-${sponsorship.id_comissao}`}
                        >
                          ver parcelas
                        </Link>
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}

          <Pagination className="p-0">
            <PaginationContent>
              <PaginationPrevious
                onClick={handlePreviousButton}
                disabled={currentPage === 1}
                data-testid="backoffice-preservers-pagination-previous"
              />

              {getVisiblePages(numberOfPages, currentPage).map(
                (page, index) => (
                  <PaginationButton
                    key={page}
                    isActive={currentPage === page}
                    onClick={() => setCurrentPage(page)}
                    className={
                      index === 1
                        ? 'border-x-monochromatic-300 border-x'
                        : undefined
                    }
                    data-testid={`backoffice-preservers-pagination-page-${page}`}
                  >
                    {page}
                  </PaginationButton>
                ),
              )}

              <PaginationNext
                onClick={handleNextButton}
                disabled={currentPage === numberOfPages || numberOfPages === 0}
                data-testid="backoffice-preservers-pagination-next"
              />
            </PaginationContent>

            <PaginationItemsPerPage
              isDisabled={Number(sponsorships?.total_comissoes) <= 10}
              handleItemsPerPage={(value) => {
                setItemsPerPage(value)
                setCurrentPage(1)
              }}
              itemsPerPage={itemsPerPage}
              data-testid="backoffice-preservers-items-per-page"
            />

            <PaginationCurrentPage
              itemsPerPage={Number(itemsPerPage)}
              currentPage={currentPage}
              totalOfItems={sponsorships?.total_comissoes || 0}
            />
          </Pagination>
        </div>
      </div>
    </div>
  )
}
